import { ethers } from "ethers";

// ─────────────────────────────────────────────────────────────────────────
// Single resilient read-only provider, shared by every route/component.
//
// Strategy: Infura (keyed) is ALWAYS tried first. Only if the request fails
// at the HTTP/network level (timeout, 429 rate-limit, 5xx, offline...) does
// the same request retry against the public fallbacks, in order. JSON-RPC
// level errors (e.g. contract reverts) are returned normally — they are
// valid answers, not endpoint failures.
//
// `staticNetwork` pins mainnet so ethers never fires the eth_chainId probe.
// One instance at module load lets ethers batch concurrent calls internally.
// ─────────────────────────────────────────────────────────────────────────

const INFURA_KEY = process.env.REACT_APP_INFURA_API_KEY || "a401f99315474f15bf32bb81404d0886";

const RPC_URLS = [
    `https://mainnet.infura.io/v3/${INFURA_KEY}`,   // primary (keyed)
    "https://ethereum-rpc.publicnode.com",          // fallback 1 (public)
    "https://eth-mainnet.public.blastapi.io",       // fallback 2 (public)
];

const MAINNET = ethers.Network.from(1);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Infura rate-limits INSIDE otherwise-successful batches: HTTP 200, but some
// entries are replaced by a bare `{code:-32005, message:"Too Many Requests"}`
// object with no `id` — ethers then throws BAD_DATA ("missing response for
// request"). Detect those entries so we can back off / fall back instead.
const isRateLimited = (resp) =>
    resp.some((r) =>
        r?.code === -32005 ||
        r?.error?.code === -32005 ||
        r?.error?.code === 429 ||
        (r && r.id === undefined && r.result === undefined)
    );

class ResilientJsonRpcProvider extends ethers.JsonRpcProvider {
    constructor(urls, network) {
        // batchMaxCount keeps single HTTP payloads small enough that a burst
        // (e.g. a page of tokens loading) doesn't slam the endpoint at once.
        super(urls[0], network, { staticNetwork: network, batchMaxCount: 10 });
        this.urls = urls;
    }

    // Mirrors ethers v6 JsonRpcProvider._send, but:
    //  - retries the SAME endpoint with backoff when rate-limited (batch-level
    //    -32005 included), since the limit is transient;
    //  - walks the URL list on transport failure or persistent rate-limiting.
    // Safe to re-send whole payloads: everything through here is a read.
    async _send(payload) {
        let lastError;
        for (const url of this.urls) {
            for (let attempt = 0; attempt < 3; attempt++) {
                try {
                    const request = new ethers.FetchRequest(url);
                    request.body = JSON.stringify(payload);
                    request.setHeader("content-type", "application/json");
                    const response = await request.send();
                    response.assertOk();
                    let resp = response.bodyJson;
                    if (!Array.isArray(resp)) { resp = [resp]; }
                    if (isRateLimited(resp)) {
                        lastError = new Error(`rate limited by ${new URL(url).host}`);
                        await sleep(400 * (attempt + 1)); // 400ms, 800ms
                        continue;
                    }
                    return resp;
                } catch (error) {
                    lastError = error;
                    break; // transport error → next endpoint, don't retry here
                }
            }
            console.warn(`RPC endpoint ${new URL(url).host} failed, trying next fallback`, lastError?.shortMessage || lastError?.message);
        }
        throw lastError;
    }
}

export const infuraProvider = new ResilientJsonRpcProvider(RPC_URLS, MAINNET);
